import { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import {
  JobContactsRepository,
  JobRecord,
  loadJobContactData,
} from '../../components/job-workspace/jobWorkspaceModel';

export const supabaseJobContactsRepository: JobContactsRepository = {
  async listStaff() {
    const { data } = await supabase.from('staff_members').select('*').order('full_name');
    return data;
  },
  async listLeads(query) {
    const { data, error } = await supabase.from('event_leads').select('*').or(query).order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },
  async listReferralAttributions(leadIds) {
    const { data } = await supabase
      .from('referrals')
      .select('event_lead_id, partner_programme_type_snapshot, partner_display_name_snapshot')
      .in('event_lead_id', leadIds);
    return data || [];
  },
  async findCompanyIdByName(name) {
    const { data } = await supabase.from('companies').select('id').ilike('name', name).maybeSingle();
    return data?.id || null;
  },
  async createCompany(name) {
    const { data } = await supabase.from('companies').insert([{ name }]).select().single();
    return data?.id || null;
  },
  async linkLeadToCompany(leadId, companyId) {
    await supabase.from('event_leads').update({ company_id: companyId }).eq('id', leadId);
  },
};

interface UseJobContactsOptions {
  isOpen: boolean;
  activeContact: JobRecord | null;
  isCompanyProfile: boolean;
  repository?: JobContactsRepository;
}

export const useJobContacts = ({
  isOpen,
  activeContact,
  isCompanyProfile,
  repository = supabaseJobContactsRepository,
}: UseJobContactsOptions) => {
  const [inquiryHistory, setInquiryHistory] = useState<JobRecord[]>([]);
  const [staffList, setStaffList] = useState<JobRecord[]>([]);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = activeContact ? `${isCompanyProfile ? 'company' : 'contact'}:${activeContact.id}` : null;
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !activeContact) return;
    let cancelled = false;
    const fetchInquiriesAndStaff = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await loadJobContactData(activeContact, isCompanyProfile, repository);
        if (cancelled) return;
        if (result.staff) setStaffList(result.staff);
        setInquiryHistory(result.leads);
        setLoadedKey(key);
      } catch {
        if (!cancelled) { setInquiryHistory([]); setLoadedKey(key); setError('Unable to load workspace records. Reopen to retry.'); }
      } finally { if (!cancelled) setIsLoading(false); }
    };
    void fetchInquiriesAndStaff();
    return () => { cancelled = true; };
  }, [isOpen, activeContact, isCompanyProfile, repository, key]);

  useEffect(() => {
    if (!isOpen) setInquiryHistory([]);
  }, [isOpen]);

  return { inquiryHistory: loadedKey === key && isOpen ? inquiryHistory : [], setInquiryHistory, staffList, isLoading, error };
};
